/**
 * Reports: monthly cash flow, category breakdown, currency exposure.
 *
 * Everything here is in historical base value — a report about March must not
 * change because a rate moved this week. The FX exposure tab is the one place
 * that deliberately contrasts historical cost with today's value, because the
 * gap between them is the thing being reported.
 */
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ComposedChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/misc';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState, ErrorState, PageHeader, StatCard } from '@/components/common';
import { chartColor } from '@/lib/colors';
import {
  formatMoney,
  formatMonth,
  formatMonthShort,
  formatPercent,
  toIsoDate,
} from '@/lib/format';
import { useByCategory, useCashflow, useFunds, useFxExposure } from '@/hooks/useApi';

function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth() - 11, 1);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

export function ReportsPage(): JSX.Element {
  const [range, setRange] = useState(defaultRange);
  const [fundId, setFundId] = useState<number | undefined>(undefined);

  const { data: funds = [] } = useFunds();
  const params = { ...range, fundId };

  return (
    <div>
      <PageHeader title="Справки" description="Всички суми са в базовата валута." />

      <Card className="mb-6">
        <CardContent className="grid gap-3 p-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="r-from">От</Label>
            <Input
              id="r-from"
              type="date"
              value={range.from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-to">До</Label>
            <Input
              id="r-to"
              type="date"
              value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Каса</Label>
            <Select
              value={fundId ? String(fundId) : 'all'}
              onValueChange={(v) => setFundId(v === 'all' ? undefined : Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всички каси</SelectItem>
                {funds.map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="cashflow">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="cashflow" className="flex-1 sm:flex-none">
            Паричен поток
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex-1 sm:flex-none">
            Категории
          </TabsTrigger>
          <TabsTrigger value="fx" className="flex-1 sm:flex-none">
            Валути
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cashflow">
          <CashflowTab params={params} />
        </TabsContent>
        <TabsContent value="categories">
          <CategoriesTab params={params} />
        </TabsContent>
        <TabsContent value="fx">
          <FxTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────── cash flow ───────────────────────────

function CashflowTab({
  params,
}: {
  params: { from: string; to: string; fundId?: number };
}): JSX.Element {
  const { data, isLoading, error, refetch } = useCashflow(params);

  const chartData = useMemo(() => {
    if (!data) return [];
    const d = 10 ** data.baseDecimals;
    return data.months.map((m) => ({
      month: m.month,
      Приходи: m.incomeMinor / d,
      Разходи: -(m.expenseMinor / d),
      Нето: (m.incomeMinor - m.expenseMinor) / d,
    }));
  }, [data]);

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isLoading || !data) return <Card className="h-80 animate-pulse" />;

  const { totals, baseCurrency, baseDecimals } = data;
  const net = totals.incomeMinor - totals.expenseMinor;
  const hasData = totals.incomeMinor > 0 || totals.expenseMinor > 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          label="Приходи"
          value={
            <span className="tabular text-income">
              {formatMoney(totals.incomeMinor, baseCurrency, baseDecimals)}
            </span>
          }
        />
        <StatCard
          label="Разходи"
          value={
            <span className="tabular text-expense">
              {formatMoney(totals.expenseMinor, baseCurrency, baseDecimals)}
            </span>
          }
        />
        <StatCard
          label="Нето"
          value={
            <span className={cn('tabular', net > 0 ? 'text-income' : net < 0 ? 'text-expense' : '')}>
              {net > 0 ? '+' : net < 0 ? '−' : ''}
              {formatMoney(Math.abs(net), baseCurrency, baseDecimals)}
            </span>
          }
          sub={
            params.fundId && (totals.transferInMinor > 0 || totals.transferOutMinor > 0)
              ? `Прехвърляния: +${formatMoney(totals.transferInMinor, baseCurrency, baseDecimals)} / −${formatMoney(totals.transferOutMinor, baseCurrency, baseDecimals)}`
              : undefined
          }
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">По месеци</CardTitle>
        </CardHeader>
        <CardContent>
          {!hasData ? (
            <EmptyState
              icon={BarChart3}
              title="Няма движения в този период"
              description="Промени периода или касата."
            />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickFormatter={formatMonthShort}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelFormatter={(v: string) => formatMonth(v)}
                    formatter={(v: number, name: string) => [
                      formatMoney(
                        Math.round(Math.abs(v) * 10 ** baseDecimals),
                        baseCurrency,
                        baseDecimals,
                      ),
                      name,
                    ]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Приходи" fill="hsl(var(--income))" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Разходи" fill="hsl(var(--expense))" radius={[0, 0, 3, 3]} />
                  <Line
                    type="monotone"
                    dataKey="Нето"
                    stroke="hsl(var(--foreground))"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────── categories ───────────────────────────

function CategoriesTab({
  params,
}: {
  params: { from: string; to: string; fundId?: number };
}): JSX.Element {
  const [kind, setKind] = useState<'EXPENSE' | 'INCOME'>('EXPENSE');
  const { data, isLoading, error, refetch } = useByCategory({ ...params, kind });

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isLoading || !data) return <Card className="h-80 animate-pulse" />;

  const { rows, baseCurrency, baseDecimals } = data;
  const total = rows.reduce((n, r) => n + r.totalMinor, 0);

  const pieData = rows.slice(0, 10).map((r, i) => ({
    name: r.categoryName,
    value: r.totalMinor / 10 ** baseDecimals,
    fill: chartColor(i),
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:max-w-xs">
        {(
          [
            ['EXPENSE', 'Разходи'],
            ['INCOME', 'Приходи'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setKind(value)}
            className={cn(
              'h-10 rounded-md border text-sm font-medium transition-colors',
              kind === value ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={BarChart3}
            title="Няма данни за този период"
            description="Промени периода или касата."
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Разпределение</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="55%"
                      outerRadius="85%"
                      paddingAngle={2}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} stroke="hsl(var(--background))" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(v: number, name: string) => [
                        formatMoney(Math.round(v * 10 ** baseDecimals), baseCurrency, baseDecimals),
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Общо {formatMoney(total, baseCurrency, baseDecimals)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {rows.map((r, i) => (
                <div key={`${r.categoryId ?? 'none'}-${i}`} className="space-y-1">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: chartColor(i) }}
                      />
                      <span className="truncate">{r.categoryName}</span>
                    </span>
                    <span className="tabular shrink-0">
                      {formatMoney(r.totalMinor, baseCurrency, baseDecimals)}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {formatPercent(r.share)}
                      </span>
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${r.share * 100}%`, background: chartColor(i) }}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── fx exposure ───────────────────────────

function FxTab(): JSX.Element {
  const { data, isLoading, error, refetch } = useFxExposure();

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isLoading || !data) return <Card className="h-64 animate-pulse" />;

  const { rows, baseCurrency, baseDecimals, asOf } = data;
  const foreign = rows.filter((r) => r.currency !== baseCurrency);

  if (rows.length === 0) {
    return (
      <Card>
        <EmptyState icon={BarChart3} title="Няма наличности" />
      </Card>
    );
  }

  const totalUnrealised = foreign.reduce((n, r) => n + r.unrealisedMinor, 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Наличности по валути</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="pb-2 font-medium">Валута</th>
                  <th className="pb-2 text-right font-medium">Наличност</th>
                  <th className="pb-2 text-right font-medium">Стойност днес</th>
                  <th className="pb-2 text-right font-medium">Историческа</th>
                  <th className="pb-2 text-right font-medium">Разлика</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.currency} className="border-b last:border-0">
                    <td className="py-2 font-medium">{r.currency}</td>
                    <td className="tabular py-2 text-right">
                      {formatMoney(r.nativeMinor, r.currency, r.decimals)}
                    </td>
                    <td className="tabular py-2 text-right">
                      {formatMoney(r.baseMinor, baseCurrency, baseDecimals)}
                    </td>
                    <td className="tabular py-2 text-right text-muted-foreground">
                      {formatMoney(r.baseCostMinor, baseCurrency, baseDecimals)}
                    </td>
                    <td
                      className={cn(
                        'tabular py-2 text-right',
                        r.unrealisedMinor > 0
                          ? 'text-income'
                          : r.unrealisedMinor < 0
                            ? 'text-expense'
                            : 'text-muted-foreground',
                      )}
                    >
                      {r.unrealisedMinor === 0
                        ? '—'
                        : `${r.unrealisedMinor > 0 ? '+' : '−'}${formatMoney(Math.abs(r.unrealisedMinor), baseCurrency, baseDecimals)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 space-y-1 border-t pt-4 text-xs text-muted-foreground">
            <p>Курсове към {asOf}.</p>
            <p>
              „Историческа" е стойността по курса от деня на всяка операция — това ползват
              справките за паричния поток. „Стойност днес" е преоценка по текущия курс. Разликата
              между двете е нереализирана и не се осчетоводява.
            </p>
            {totalUnrealised !== 0 ? (
              <p className="pt-1 text-sm text-foreground">
                Общо нереализирана разлика:{' '}
                <span
                  className={cn(
                    'tabular font-medium',
                    totalUnrealised > 0 ? 'text-income' : 'text-expense',
                  )}
                >
                  {totalUnrealised > 0 ? '+' : '−'}
                  {formatMoney(Math.abs(totalUnrealised), baseCurrency, baseDecimals)}
                </span>
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {foreign.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Тежест по валути</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={rows.map((r) => ({
                    currency: r.currency,
                    value: r.baseMinor / 10 ** baseDecimals,
                  }))}
                  margin={{ top: 5, right: 5, bottom: 5, left: -20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="currency"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v: number) => [
                      formatMoney(Math.round(v * 10 ** baseDecimals), baseCurrency, baseDecimals),
                      'Стойност',
                    ]}
                  />
                  <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                    {rows.map((_, i) => (
                      <Cell key={i} fill={chartColor(i)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
